// Retain only a payload fingerprint + random request key in session storage.
// A timed-out POST may already have launched; retry must not mean another job.
function canonical(value) {
  if (Array.isArray(value)) return '[' + value.map(canonical).join(',') + ']';
  if (value && typeof value === 'object') return '{' + Object.keys(value).sort()
    .filter(k => value[k] !== undefined).map(k => JSON.stringify(k) + ':' + canonical(value[k])).join(',') + '}';
  return JSON.stringify(value);
}

const slot = context => 'goose:build-start:v1:' + encodeURIComponent(context);
export function listBuildStartRequests(context, storage = globalThis.sessionStorage) {
  if (typeof context !== 'string' || !context) return [];
  const raw = storage.getItem(slot(context));
  if (!raw) return [];
  let rows;
  try { rows = JSON.parse(raw); } catch { throw Error('Saved build request history is unreadable; do not resubmit until it is recovered.'); }
  if (!Array.isArray(rows) || rows.some(r => !r || !/^[a-f0-9-]{36}$/.test(r.key)
    || !/^[a-f0-9]{64}$/.test(r.fingerprint))) throw Error('Saved build request history is invalid.');
  return rows;
}
export function forgetBuildStartRequest(context, key, storage = globalThis.sessionStorage) {
  const rows = listBuildStartRequests(context, storage), row = rows.find(r => r.key === key);
  if (row && (row.pending !== false || row.dispatch_status !== 'published')) throw Error('This request is not confirmed. Check its status before starting another build.');
  storage.setItem(slot(context), JSON.stringify(rows.filter(r => r.key !== key)));
}
export function recordBuildStartResult(context, key, result, storage = globalThis.sessionStorage) {
  const current = listBuildStartRequests(context, storage);
  storage.setItem(slot(context), JSON.stringify(current.map(r => r.key === key ? { ...r,
    job_id: result.job_id || r.job_id, pending: !!result.pending, dispatch_status: result.dispatch_status } : r)));
}
export async function buildStartRequest(payload, context, { storage = globalThis.sessionStorage,
  crypto = globalThis.crypto } = {}) {
  if (typeof context !== 'string' || !context) throw Error('Reload your workspace before starting a build.');
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(canonical([context, payload])));
  const fingerprint = [...new Uint8Array(digest)].map(n => n.toString(16).padStart(2, '0')).join('');
  const rows = listBuildStartRequests(context, storage), existing = rows.find(r => r.fingerprint === fingerprint);
  const key = existing?.key || crypto.randomUUID();
  // Failure is visible, not a silent memory-only fallback that duplicates on reload.
  if (!existing) storage.setItem(slot(context), JSON.stringify([...rows,
    { key, fingerprint, name: payload.test_id || 'Build request', pending: true }]));
  if (!listBuildStartRequests(context, storage).some(r => r.key === key)) throw Error('Cannot safely retain this build request. Enable session storage and retry.');
  return { key, retained: !!existing, record: result => recordBuildStartResult(context, key, result, storage) };
}
